import { throttle } from 'lodash-es';

import { skyFoundationSettings } from '../../services/configs/configs-service';
import { getCarouselData } from '../carousel/carousel-data';
import {
  leftArrowTemplate,
  rightArrowTemplate,
  removePrevArrow,
  removeNextArrow,
} from '../carousel/carousel-templates';
import { tabsRenderer, tabsClick, getInitialTabsData } from '../playlist-tabs/playlist-tabs';
import { getLabel } from '../../labels/labelling-service';
import { initPlayers } from '../video-player/init-players';
import { isMobile } from '../../utils/device-utils';
import logger from '../../services/logging/logging-service';

import {
  initVideoPlaylist,
  preparePlaylistPlayer,
  hideVideoPlayer,
  showVideoPlayer,
  removePlaylistContainer,
} from './video-playlist';

const config = require('config');

const BASE_PLAYLIST_ID = 'video-playlist-id-';

const VIDEO_CAROUSEL_LOADING = 'j-player-loading';
const VIDEO_CAROUSEL_ERROR_MESSAGE = 'j-player-loading-message';
const VIDEO_CAROUSEL_LOADER = 'j-player-loading-loader';

const loadingModalRef = document.querySelector(`#${VIDEO_CAROUSEL_LOADING}`);
const messageRef = document.querySelector(`#${VIDEO_CAROUSEL_ERROR_MESSAGE}`);

const KEY_PRESS_THROTTLE_TIME = 350;

const PLAYLIST_BASE_URL = skyFoundationSettings.getPlaylistBaseUrl();

let currentCarouselId;
let selectedTabId;
let tabsMap = new Map();
let playlist;

/**
 * Dynamic build new Carousel Id
 * @param {number} tabIndex the index of the tab
 */
const buildCarouselId = (tabIndex) => BASE_PLAYLIST_ID + tabIndex;

/**
 * Clear error
 */
const clearError = () => {
  if (loadingModalRef) {
    loadingModalRef.style.display = 'none';
    messageRef.innerHTML = '';
  }
  showVideoPlayer();
};

/**
 * Set new playlist to the video carousel
 * @param {object} data the data carousel to set
 * @param {object} playlist the current playlist
 * @param {string} carouselId new carousel Id
 */
const setPlaylist = (data, playlistToSet, carouselId) => {
  if (selectedTabId === carouselId || !selectedTabId) {
    if (playlistToSet !== undefined) {
      playlistToSet.destroy(currentCarouselId);
    }
    currentCarouselId = carouselId;
    clearError();
    return initVideoPlaylist(data, carouselId);
  }
};

/**
 * Show / Hide Loading component
 * @param {boolean} isLoading if true show loading component otherwise hide component
 */
const isCarouselLoading = (isLoading = true) => {
  const display = isLoading ? 'flex' : 'none';
  if (loadingModalRef) {
    loadingModalRef.style.display = display;
    document.querySelector(`#${VIDEO_CAROUSEL_LOADER}`).style.display = isLoading ? 'block' : 'none';
    messageRef.innerHTML = '';
  }
};

/**
 * Render the carousel arrows
 * @param {number} dataLength length of the carousel data
 */
const renderArrows = (dataLength) => {
  if (!isMobile()) {
    if (dataLength > config.videoPlaylist.renderArrowsAfter) {
      rightArrowTemplate();
      leftArrowTemplate();
    } else {
      removePrevArrow();
      removeNextArrow();
    }
  }
};

/**
 * Render the error component and remove navigation arrows
 * @param {object} playlist playlist to destroy
 * @param {string} playlistId current playlistId
 */
const renderError = (playlistToSet, playlistId) => {
  logger.warn(`[ INIT - CAROUSEL ] renderError playlist:${playlistToSet} playlistId:${playlistId}`);
  if (selectedTabId === playlistId || playlistId === undefined) {
    hideVideoPlayer();
    if (loadingModalRef) {
      loadingModalRef.style.display = 'flex';
      messageRef.innerHTML = getLabel('video.playlist.error');
      document.querySelector(`#${VIDEO_CAROUSEL_LOADER}`).style.display = 'none';
    }
    if (playlistToSet) {
      playlistToSet.pause();
      playlistToSet.destroy(currentCarouselId);
    }
    removePrevArrow();
    removeNextArrow();
  }
};

/**
 * Tabs click callback
 * @param {*} element tab element clicked
 */
const tabsClickCallback = async (element) => {
  const elementData = JSON.parse(element.dataset.json);
  const carouselId = buildCarouselId(elementData.tabIndex);
  try {
    if (carouselId !== selectedTabId) {
      selectedTabId = carouselId;
      if (tabsMap.has(carouselId)) {
        clearError();
        const tabData = tabsMap.get(carouselId);
        renderArrows(tabData.length);
        playlist = setPlaylist(tabData, playlist, carouselId);
      } else {
        isCarouselLoading();
        const tabCarouselData = await getCarouselData(PLAYLIST_BASE_URL, elementData);
        isCarouselLoading(false);
        if (tabCarouselData.length > 0) {
          renderArrows(tabCarouselData.length);
          tabsMap.set(carouselId, tabCarouselData);
          playlist = setPlaylist(tabCarouselData, playlist, carouselId);
        } else {
          renderError(playlist, carouselId);
        }
      }
    }
  } catch (error) {
    renderError(playlist, carouselId);
  } finally {
    selectedTabId = carouselId;
  }
};

/**
 * Get ALL Carousel Data and collect just the good ones into an array
 * If the array is empty throw an error
 * @param {array} initialTabsData tabs-data useful to fetch the playlists
 * @returns {array} contains all the successful tabs-data responses. The elements are objects with "carouselData", "tabData" and "error" keys
 */
const getAllCarouselData = async ({ initialTabsData, hlPrevTags = false }) => {
  const arrayOfPromises = initialTabsData.map((tabData) => {
    return getCarouselData(PLAYLIST_BASE_URL, tabData, hlPrevTags)
      .then((carouselData) => {
        return { carouselData, tabData, error: false };
      })
      .catch((error) => {
        logger.warn(
          `[ INIT - CAROUSEL ] multiple-video-playlist error in getCarouselData() on tabIndex: ${tabData.tabIndex}`
        );
        return { carouselData: [], tabData, error };
      });
  });
  const allCarouselData = await Promise.all(arrayOfPromises);
  const successCarouselData = allCarouselData.filter((data) => !data.error && data.carouselData.length);
  if (!successCarouselData.length && hlPrevTags) {
    throw new Error('Error in Carousel Data: even hlPrevTags failed');
  }
  return successCarouselData;
};

/**
 * Initialize new Multiple Video Playlist
 *
 */
const initMultiplePlaylist = async () => {
  tabsMap = new Map();
  const initialTabsData = getInitialTabsData(); // Array of items (each item is a tab) coming from AEM and containing both tags & hlPrevTags.
  logger.info(`[ INIT - CAROUSEL ] tabsData ${initialTabsData}`);
  if (initialTabsData.length > 0) {
    preparePlaylistPlayer();
    try {
      isCarouselLoading();
      logger.info('[ INIT - CAROUSEL ] Start fetching playlists');
      let successCarouselData = await getAllCarouselData({ initialTabsData });
      if (!successCarouselData.length) {
        // Fallback to hlPrevTags
        successCarouselData = await getAllCarouselData({ initialTabsData, hlPrevTags: true });
      }
      const firstTabData = successCarouselData[0].tabData;
      successCarouselData.forEach((element) => {
        const carouselId = buildCarouselId(element.tabData.tabIndex);
        tabsMap.set(carouselId, element.carouselData);
      });
      isCarouselLoading(false);

      /* initialize first TAB */
      currentCarouselId = buildCarouselId(firstTabData.tabIndex);
      const initialCarouselData = tabsMap.get(currentCarouselId);
      selectedTabId = currentCarouselId;
      if (initialCarouselData.length > 0) {
        renderArrows(initialCarouselData.length);
        playlist = initVideoPlaylist(initialCarouselData, currentCarouselId);
      } else {
        /* Error in getCarousel Data */
        logger.warn('[ INIT - CAROUSEL ] multiple-video-playlist error in getCarouselData() ');
        renderError(playlist);
        hideVideoPlayer();
        removePlaylistContainer();
        throw new Error('[ INIT - CAROUSEL ] Error in Carousel Data');
      }
      const tabsData = successCarouselData.map((element) => element.tabData);
      tabsRenderer(tabsData);
      tabsClick(throttle(tabsClickCallback, KEY_PRESS_THROTTLE_TIME, { trailing: false }));
    } catch (error) {
      /* Error in getCarousel Data */
      logger.warn(`[ INIT - CAROUSEL ] multiple-video-playlist error in getCarouselData() - Error: ${error}`);
      hideVideoPlayer();
      removePlaylistContainer();
      isCarouselLoading(false);
      throw error;
    }
  } else {
    /* Error in Tabs */
    logger.warn('[ INIT - CAROUSEL ] multiple-video-playlist error in error in tabs ');
    hideVideoPlayer();
    removePlaylistContainer();
    throw new Error('[ INIT - CAROUSEL ] Error in Tabs');
  }
};

export { initMultiplePlaylist, initPlayers };
